import { ItascaOrgTier } from '@acretrader/acremaps-permissions';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { ButtonVariant } from '@/components/button';
import { PARCEL_DETAILS_URL, PLAT_MAP_URL } from '@/constants/urls';
import { useCustomizeLandContext } from '@/features/customizeMyLand/provider';
import { TabTypes, useNavigationContext } from '@/features/navigation/provider';
import { useParcelDetails } from '@/features/parcelDetails/hooks/useParcelDetails';
import { CardFooterButton } from '@/features/selectionsPanel/selectionCard/baseCard/cardFooterButton';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';
import { IParcelFeature } from '@/models/features';
import { useAuth } from '@/providers/auth-provider';
import { isDefined } from '@/utils/guards';

import { CardProps } from '../../constants';
import { useGenericSelectionPanelFunctions } from '../../hooks/useGenericSelectionPanelFunctions';
import { useGetLocationInfo } from '../../hooks/useGetLocationInfo';
import { Card } from '../baseCard/card';
import { CardTitle } from '../baseCard/cardTitle';
import { LineItem } from '../baseCard/lineItem';

const formatName = (s?: string) => {
  return !isDefined(s) ? '' : s.toLowerCase();
};

export const ParcelCard: FC<CardProps<IParcelFeature>> = ({ feature, idx }) => {
  const router = useRouter();
  const params = useParams();
  const pathname = usePathname();
  const { userCanAccessOwnerNames, isFreeOrAbove } = useAuth();
  const { isCustomizing } = useCustomizeLandContext();
  const [isHovered, setIsHovered] = useState(false);
  const { removeFeature, handleMouseOn, handleMouseOff, goToGeometryLocation } =
    useGenericSelectionPanelFunctions();
  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);
  const { setCurrentMobileTab } = useNavigationContext();
  const [cardHasBeenViewed, setCardHasBeenViewed] = useState(false);

  const clip = useMemo(() => {
    const all = params?.all;
    const parcelClipFromRoute = all?.[2];

    return parcelClipFromRoute;
  }, [params]);

  const forceHoveredStyles = useMemo(
    () => feature.properties.clip === clip,
    [clip, feature.properties.clip]
  );

  const {
    state,
    county,
    text: locationText,
  } = useGetLocationInfo({
    feature,
    shouldFetch: cardHasBeenViewed,
  });

  // Fetch the parcel details only if the card has entered the viewport.
  const { parcel, isLoading } = useParcelDetails(
    cardHasBeenViewed ? feature.properties.clip : ''
  );

  const loadedOwnerName = useMemo(() => {
    if (parcel) {
      return `${formatName(parcel.own1_frst)} ${formatName(
        parcel.own1_last
      )}`.toLowerCase();
    }
    return '–';
  }, [parcel]);

  const isAlreadyOnDetailsPage = useMemo(() => {
    if (!pathname) return false;
    if (!feature.id && !feature.properties.clip) return false;
    
    return (
      pathname.includes(String(feature.id)) ||
      pathname.includes(feature.properties.clip)
    );
  }, [feature, pathname]);

  const handleShowingParcelDetails = useCallback(() => {
    if (!feature?.properties?.clip || !county || !state) return;

    if (isMobile) {
      setCurrentMobileTab(TabTypes.MAP);
    }

    router.push(
      `${PARCEL_DETAILS_URL}/${state}/${county}/${feature.properties.clip}`
    );
  }, [
    feature.properties.clip,
    county,
    state,
    isMobile,
    router,
    setCurrentMobileTab,
  ]);

  const handleHideParcelDetails = useCallback(() => {
    router.push(PLAT_MAP_URL);
  }, [router]);

  const handleOnClick = useCallback(
    (e) => {
      e.stopPropagation();
      goToGeometryLocation(feature);
    },
    [feature, goToGeometryLocation]
  );

  const handleOnClose = useCallback(() => {
    removeFeature(feature);
  }, [feature, removeFeature]);

  const handleSettingHoverOnOwnerName = useCallback(
    (value) => {
      if (!userCanAccessOwnerNames) {
        setIsHovered(value);
      }
    },
    [userCanAccessOwnerNames]
  );

  useEffect(() => {
    if (isHovered) {
      handleMouseOn(feature);
    } else {
      handleMouseOff(feature);
    }
  }, [feature, handleMouseOff, handleMouseOn, isHovered]);

  /**
   * BEGIN NASTY
   * Ethan Zitting - July 5th, 2024
   * This is a nasty bit of code that delays the rendering of this card.
   * The card component is reliant on a variable from the FileManagementProvider.
   * Delaying the render of the card allows the FileManagementProvider to update the value before the card is rendered.
   */
  const [showComponent, setShowComponent] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => setShowComponent(true), 10);

    return () => clearTimeout(timeout);
  }, []);
  if (!showComponent) return null;
  // END NASTY

  return (
    <Card
      className="cursor-pointer"
      forceHovered={forceHoveredStyles}
      onCardInView={() => {
        if (!cardHasBeenViewed) setCardHasBeenViewed(true);
      }}
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex flex-col gap-2">
        <CardTitle feature={feature} idx={idx} onClose={handleOnClose} />
        <div>
          {isFreeOrAbove && (
            <div
              onMouseEnter={() => handleSettingHoverOnOwnerName(false)}
              onMouseLeave={() => handleSettingHoverOnOwnerName(true)}
            >
              <LineItem
                isLoading={isLoading}
                label="Owner"
                requiredTier={ItascaOrgTier.plus}
                value={loadedOwnerName}
              />
            </div>
          )}

          <LineItem label="Location" value={locationText} />
          <LineItem
            className="truncate"
            isLoading={isLoading && !feature.properties.apn}
            label="Parcel #"
            requiredTier={ItascaOrgTier.free}
            value={parcel?.apn ?? feature.properties?.apn}
          />
        </div>
        {feature.properties.clip &&
          (isAlreadyOnDetailsPage ? (
            <CardFooterButton
              disabled={isCustomizing}
              text="Hide Details"
              variant={ButtonVariant.PRIMARY_LIGHT}
              onClick={handleHideParcelDetails}
            />
          ) : (
            <CardFooterButton
              disabled={isCustomizing}
              text="View Details"
              onClick={handleShowingParcelDetails}
            />
          ))}
      </div>
    </Card>
  );
};
